import { Box, Flex, Radio, Stack, Text } from '@mantine/core';
import pluralize from 'pluralize';
import { useState } from 'react';
import { useFamilies } from 'src/clients/hooks/useFamilies';
import { Family } from 'src/clients/hooks/useFamily';
import { FilterHeader } from 'src/common/components/table/SearchHeader';
import { useCurrentClientIfAvailable } from '../context/CurrentClientProvider';
import { useParams, useLocation } from 'react-router-dom';

export const FAMILY_SELECTION_MODE = {
  SINGLE: 'single',
} as const;

type Props = {
  selectedFamily?: Family;
  onSelectFamily: (family: Family) => void;
  showBorder?: boolean;
  selectionMode?: (typeof FAMILY_SELECTION_MODE)[keyof typeof FAMILY_SELECTION_MODE];
  SearchHeaderComponent?: React.ComponentType<{
    searchTerm: string;
    setSearchTerm: (term: string) => void;
  }>;
};

function useCurrentFamilyId() {
  const { id } = useParams();
  const location = useLocation();
  const isFamiliesPath = location.pathname.startsWith('/clients/families/');
  const currentFamilyId = isFamiliesPath ? id : null;

  return currentFamilyId;
}

export function FamilyList({
  selectedFamily,
  onSelectFamily,
  showBorder = false,
  SearchHeaderComponent,
}: Props) {
  const { families } = useFamilies();
  const [searchTerm, setSearchTerm] = useState('');
  const { client: currentClient } = useCurrentClientIfAvailable();
  const currentFamilyId = useCurrentFamilyId();

  const filteredFamilies = families
    .filter(family => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return family.name.toLowerCase().includes(searchLower);
    })
    .sort((a, b) => {
      // Move current family to top
      if (currentFamilyId) {
        if (a.id === currentFamilyId) {
          return -1;
        }
        if (b.id === currentFamilyId) {
          return 1;
        }
      }

      // Move family with current client to top
      if (currentClient) {
        if (
          a.mainFamilyMember.clientId === currentClient.id ||
          a.secondaryFamilyMembers.some(member => member.clientId === currentClient.id)
        ) {
          return -1;
        }
        if (
          b.mainFamilyMember.clientId === currentClient.id ||
          b.secondaryFamilyMembers.some(member => member.clientId === currentClient.id)
        ) {
          return 1;
        }
      }

      // Sort alphabetically by name
      return a.name.localeCompare(b.name);
    });

  const SelectControl = Radio;

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      {SearchHeaderComponent ? (
        <SearchHeaderComponent searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      ) : (
        <FilterHeader
          direction="row-reverse"
          mb="md"
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          inputProps={{
            maw: undefined,
          }}
        />
      )}
      <Stack
        gap="xs"
        p={showBorder ? 'md' : undefined}
        bd={showBorder ? '1px solid var(--mantine-color-gray-1)' : undefined}
        bg={showBorder ? 'white' : undefined}
        style={{
          overflowY: 'scroll',
          scrollbarWidth: 'thin',
          borderRadius: showBorder ? 12 : undefined,
        }}
      >
        {filteredFamilies.map(family => (
          <Flex
            key={family.id}
            align="center"
            gap="xs"
            onClick={() => onSelectFamily(family)}
            style={{
              cursor: 'default',
            }}
          >
            <SelectControl
              checked={selectedFamily?.id === family.id}
              aria-label={`Select ${family.name} family`}
              onChange={() => {}}
            />
            <Flex direction="column">
              <Text size="sm" fw={500}>
                {family.name}
              </Text>
              <Text size="xs" c="dimmed">
                {pluralize('member', 1 + family.secondaryFamilyMembers.length, true)}
              </Text>
            </Flex>
          </Flex>
        ))}
      </Stack>
    </Box>
  );
}
